import { downloadCsvText } from './accounting-export';

/**
 * CSV templates for the "Start Fresh" and "Starting Balances" importers.
 *
 * Each template is a header row plus a few sample rows the user replaces with
 * their own data. Files are re-identified on upload purely by header
 * signature (see `classifyTemplateCsv`), so users can rename the files or
 * re-save them through Excel without breaking anything.
 */

export type CsvTemplateKind =
	| 'customers'
	| 'owners'
	| 'bank-accounts'
	| 'gl-balances'
	| 'customer-balances'
	| 'par-values'
	| 'budget';

export interface CsvTemplate {
	kind: CsvTemplateKind;
	filename: string;
	title: string;
	description: string;
	headers: string[];
	sampleRows: string[][];
}

export const CSV_TEMPLATES: Record<CsvTemplateKind, CsvTemplate> = {
	'customers': {
		kind: 'customers',
		filename: 'customers-template.csv',
		title: 'Customers',
		description: 'One row per unit/property account. Owners are linked separately via the Owners CSV.',
		headers: ['account_number', 'address', 'lot_number'],
		sampleRows: [
			['101', '123 Main St, Springfield IL 62704', 'L-101'],
			['102', '125 Main St, Springfield IL 62704', 'L-102'],
			['103', '127 Main St, Springfield IL 62704', ''],
		],
	},
	'owners': {
		kind: 'owners',
		filename: 'owners-template.csv',
		title: 'Owners',
		description: 'One row per person; repeat the account number for co-owners. Accounts must exist (Customers CSV).',
		headers: ['account_number', 'first_name', 'last_name', 'email', 'phone', 'notes'],
		sampleRows: [
			['101', 'John', 'Spencer', 'john.spencer@example.com', '555-0101', ''],
			['101', 'Mary', 'Spencer', 'mary.spencer@example.com', '', 'Co-owner'],
			['102', 'Alice', 'Wong', 'alice.wong@example.com', '555-0102', ''],
		],
	},
	'bank-accounts': {
		kind: 'bank-accounts',
		filename: 'bank-accounts-template.csv',
		title: 'Bank Accounts',
		description: 'kind is one of: checking, savings, money_market, cd, other. gl_code is the cash GL the account posts to (1010/1020 in the default chart). Only the LAST 4 digits of account/routing numbers — full numbers are entered later through the encrypted secure-fields flow.',
		headers: ['name', 'kind', 'institution', 'gl_code', 'account_number_last4', 'routing_number_last4', 'notes'],
		sampleRows: [
			['Operating Checking', 'checking', 'First National Bank', '1010', '4321', '0210', ''],
			['Reserve Money Market', 'money_market', 'First National Bank', '1020', '8765', '0210', 'Reserve fund'],
		],
	},
	'gl-balances': {
		kind: 'gl-balances',
		filename: 'gl-starting-balances-template.csv',
		title: 'GL Starting Balances',
		description: 'Opening balance per GL account, entered as the account\'s NORMAL balance: positive for what an asset/expense holds, positive for what a liability/income account carries. account_type (BANK, OCASSET, FIXASSET, AP, OCLIAB, LTLIAB, INC, EXP, EQUITY) lets the import create a GL code that doesn\'t exist yet.',
		headers: ['gl_code', 'gl_name', 'account_type', 'balance'],
		sampleRows: [
			['1010', 'CHECKING - US Bank', 'BANK', '12345.67'],
			['1020', 'MONEY MARKET - US Bank', 'BANK', '50000.00'],
			['1350', 'Prior Management Accounts', 'OCASSET', '2500.00'],
		],
	},
	'customer-balances': {
		kind: 'customer-balances',
		filename: 'customer-starting-balances-template.csv',
		title: 'Customers & Starting Balances',
		description: 'One row per owner. Each row creates the customer account; balance is what they owe at cut-over: positive = owes (opening charge), negative = prepaid (opening credit), blank or 0 = roster only (no opening balance). New accounts are created from the contact fields. pct_ownership is optional — set it for par-value associations, where each owner is billed that share of the annual budget. It accepts "4.23" or "4.23%", and every owner\'s share should total 100%.',
		headers: ['account_number', 'address', 'lot_number', 'first_name', 'last_name', 'email', 'phone', 'balance', 'pct_ownership'],
		sampleRows: [
			['101', '123 Main St, Springfield IL 62704', 'L-101', 'John', 'Spencer', 'john.spencer@example.com', '555-0101', '350.00', '4.23%'],
			['102', '125 Main St, Springfield IL 62704', 'L-102', 'Alice', 'Wong', 'alice.wong@example.com', '555-0102', '-125.00', '3.13%'],
			['103', '127 Main St, Springfield IL 62704', 'L-103', 'Sam', 'Carter', 'sam.carter@example.com', '555-0103', '', '8.5%'],
		],
	},
	'par-values': {
		kind: 'par-values',
		filename: 'ownership-percentages-template.csv',
		title: 'Ownership Percentages',
		description: 'One row per EXISTING customer, setting only that account\'s par-value ownership share. Accepts "4.23" or "4.23%", and every owner\'s share should total 100%. Nothing else on the account is touched — no addresses are overwritten and no balances are posted. Accounts that don\'t match are reported and skipped, never created.',
		headers: ['account_number', 'pct_ownership'],
		sampleRows: [
			['101', '4.23%'],
			['102', '3.13%'],
			['103', '8.5%'],
		],
	},
	'budget': {
		kind: 'budget',
		filename: 'annual-budget-template.csv',
		title: 'Annual Budget by GL',
		description: 'One row per GL code, with that code\'s ANNUAL budget. The total becomes the association\'s annual operating budget, and in par-value mode each owner is billed their ownership percentage of it, split across these codes. 3000 (Operating Assessments) and 3010 (Capital Reserve Assessments) are the usual two.',
		headers: ['gl_code', 'gl_name', 'annual_amount'],
		sampleRows: [
			['3000', 'Operating Assessments', '146000.00'],
			['3010', 'Capital Reserve Assessments', '6500.00'],
		],
	},
};

export { parsePercentToBps } from '@/utils/par-value';

const csvCell = (v: string): string => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);

export const buildTemplateCsv = (kind: CsvTemplateKind): string => {
	const t = CSV_TEMPLATES[kind];
	const lines = [t.headers.join(','), ...t.sampleRows.map((r) => r.map(csvCell).join(','))];
	return `${lines.join('\n')}\n`;
};

export const downloadCsvTemplate = async (kind: CsvTemplateKind): Promise<void> => {
	await downloadCsvText(CSV_TEMPLATES[kind].filename, buildTemplateCsv(kind));
};

// ── Upload-side helpers ─────────────────────────────────────────────────────

const normalizeHeader = (h: string): string => h.trim().toLowerCase().replace(/[\s-]+/g, '_');

/** Re-key a parsed row by normalized header so "Account Number" matches account_number. */
export const normalizeRow = (row: Record<string, any>): Record<string, string> => {
	const out: Record<string, string> = {};
	for (const [k, v] of Object.entries(row)) {
		out[normalizeHeader(k)] = String(v ?? '').trim();
	}
	return out;
};

/**
 * Identify which template a parsed file is, from its header signature alone.
 * Returns null for anything unrecognized (e.g. a QuickBooks report CSV —
 * callers fall back to the bespoke QB parsers).
 */
export const classifyTemplateCsv = (rows: Record<string, any>[]): CsvTemplateKind | null => {
	if (rows.length === 0) return null;
	const headers = new Set(Object.keys(rows[0]).map(normalizeHeader));
	const has = (...keys: string[]) => keys.every((k) => headers.has(k));

	// Ordered before gl-balances: both are keyed on gl_code, and only the
	// amount column tells them apart (annual_amount = a budget, balance = an
	// opening balance).
	if (has('gl_code', 'annual_amount')) return 'budget';
	if (has('gl_code', 'balance')) return 'gl-balances';
	if (has('account_number', 'balance')) return 'customer-balances';
	// AFTER customer-balances on purpose: that template also carries
	// pct_ownership, and a file with a balance column is a roster import that
	// happens to set shares — not a shares-only file.
	if (has('account_number', 'pct_ownership')) return 'par-values';
	if (has('name', 'gl_code')) return 'bank-accounts';
	if (has('account_number', 'first_name')) return 'owners';
	if (has('account_number') && (headers.has('address') || headers.has('lot_number'))) return 'customers';
	return null;
};
